"use client";
import { useMemo, useState } from "react";
import { getCountries, getCountryCallingCode } from "libphonenumber-js/min";
import type { CountryCode } from "libphonenumber-js/min";
import { DropdownSelect } from "../ui/select";
import { Input } from "../ui/input";
import type { PhoneInputProps } from "./phone-input.types";
import { PHONE_INPUT } from "./phone-input.constants";
import { cn } from "../../lib/utils";
import {
  internationalPhone,
  nationalPhone,
  phoneCountry,
} from "./phone-input.utils";

/** Display a national number while the caller owns the canonical international value. */
export function PhoneInput(props: PhoneInputProps) {
  const [selection, setSelection] = useState<CountryCode>();
  const country = selection ?? phoneCountry(props.value, props.defaultCountry);
  const options = useMemo(() => {
    const names = new Intl.DisplayNames([props.locale || PHONE_INPUT.locale], {
      type: "region",
      fallback: "code",
    });
    return getCountries()
      .map((code) => {
        const name = names.of(code);
        const label = `${name} (+${getCountryCallingCode(code)})`;
        return { value: code, label, searchText: `${code} ${label}` };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [props.locale]);
  return (
    <div className="space-y-1 text-sm">
      <label htmlFor={props.id} className={props.labelClassName}>{props.label}</label>
      <div className="flex min-w-0 gap-2">
        <DropdownSelect
          ariaLabel={props.countryLabel}
          value={country}
          valueLabel={`${country} +${getCountryCallingCode(country)}`}
          options={options}
          disabled={props.disabled}
          searchPlaceholder={props.searchPlaceholder}
          emptyLabel={props.emptyLabel}
          className={cn(props.controlClassName, "w-28 shrink-0")}
          contentClassName="max-h-72 w-80 max-w-[calc(100vw-2rem)] [&>[data-radix-select-viewport]]:max-h-72"
          onValueChange={(value) => {
            const next = value as CountryCode;
            setSelection(next);
            props.onChange(
              internationalPhone(nationalPhone(props.value, country), next),
            );
          }}
        />
        <Input
          id={props.id}
          type="tel"
          autoComplete="tel-national"
          inputMode="tel"
          required={props.required ?? true}
          aria-describedby={props.description ? `${props.id}-description` : undefined}
          className={cn(props.controlClassName, "min-w-0 flex-1")}
          value={nationalPhone(props.value, country)}
          disabled={props.disabled}
          placeholder={props.placeholder}
          maxLength={PHONE_INPUT.maxLength}
          onChange={(event) => {
            const next = internationalPhone(event.target.value, country);
            setSelection(phoneCountry(next, country));
            props.onChange(next);
          }}
        />
      </div>
      {props.description && (
        <p id={`${props.id}-description`} className="text-xs font-normal text-[#667085]">
          {props.description}
        </p>
      )}
    </div>
  );
}
